import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useMcpSessionStore } from "@/stores/mcpSessionStore";
import { api } from "../api/api";
import { hasCapability } from "./useMcpInitialization";

export type McpTool = {
  name: string;
  description?: string;
  // Support both snake_case and camelCase from different servers/SDKs
  input_schema?: unknown;
  inputSchema?: unknown;
};

export type McpResource = {
  name: string;
  uri: string;
  mimeType?: string;
  description?: string;
};

export type McpPromptArgument = {
  name: string;
  description?: string;
  required?: boolean;
};

export type McpPrompt = {
  name: string;
  description?: string;
  arguments?: McpPromptArgument[];
  input_schema?: unknown;
  inputSchema?: unknown;
};

type ToolsListResponse = { tools: McpTool[] };
type ResourcesListResponse = { resources: McpResource[] };
type PromptsListResponse = { prompts: McpPrompt[] };
type ResourceReadResponse = {
  contents: Array<{
    uri: string;
    mimeType?: string;
    text?: string;
    blob?: string;
  }>;
};

async function fetchMcpTools(projectId: string): Promise<McpTool[]> {
  const body = { jsonrpc: "2.0", id: "tools/list", method: "tools/list" };
  try {
    const { payload } = await api.mcpRpc(projectId, body);
    const data = payload as {
      result?: ToolsListResponse;
      error?: unknown;
    };
    const result = data?.result;
    if (!result || !Array.isArray(result.tools)) {
      throw new Error("Invalid MCP tools/list response");
    }
    return result.tools;
  } catch (error) {
    // Handle specific deployment/endpoint errors
    if (
      error instanceof Error &&
      error.message.includes("MCP endpoint not found")
    ) {
      throw new Error("MCP_ENDPOINT_NOT_FOUND");
    }
    throw error;
  }
}

async function fetchMcpResources(projectId: string): Promise<McpResource[]> {
  const body = {
    jsonrpc: "2.0",
    id: "resources/list",
    method: "resources/list",
  };
  try {
    const { payload } = await api.mcpRpc(projectId, body);
    const data = payload as {
      result?: ResourcesListResponse;
      error?: unknown;
    };
    const result = data?.result;
    if (!result || !Array.isArray(result.resources)) {
      throw new Error("Invalid MCP resources/list response");
    }
    return result.resources;
  } catch (error) {
    // Handle specific deployment/endpoint errors
    if (
      error instanceof Error &&
      error.message.includes("MCP endpoint not found")
    ) {
      throw new Error("MCP_ENDPOINT_NOT_FOUND");
    }
    throw error;
  }
}

async function fetchMcpPrompts(projectId: string): Promise<McpPrompt[]> {
  const body = {
    jsonrpc: "2.0",
    id: "prompts/list",
    method: "prompts/list",
  };
  try {
    const { payload } = await api.mcpRpc(projectId, body);
    const data = payload as {
      result?: PromptsListResponse;
      error?: unknown;
    };
    const result = data?.result;
    if (!result || !Array.isArray(result.prompts)) {
      throw new Error("Invalid MCP prompts/list response");
    }
    return result.prompts;
  } catch (error) {
    // Handle specific deployment/endpoint errors
    if (
      error instanceof Error &&
      error.message.includes("MCP endpoint not found")
    ) {
      throw new Error("MCP_ENDPOINT_NOT_FOUND");
    }
    throw error;
  }
}

async function fetchResourceContent(projectId: string, resourceUri: string) {
  const body = {
    jsonrpc: "2.0",
    id: "resources/read",
    method: "resources/read",
    params: { uri: resourceUri },
  };
  const { payload } = await api.mcpRpc(projectId, body);
  const data = payload as {
    result?: ResourceReadResponse;
    error?: unknown;
  };
  const result = data?.result;
  if (!result || !Array.isArray(result.contents)) {
    throw new Error("Invalid MCP resources/read response");
  }
  return result.contents[0] ?? null;
}

export function useMcpTools(projectId?: string, enabled = true) {
  return useQuery({
    queryKey: ["MCP", projectId, "tools"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      return fetchMcpTools(projectId);
    },
    enabled: enabled && !!projectId,
    retry: (failureCount, error) => {
      // Don't retry if MCP endpoint is not found (deployment issue)
      if (error?.message === "MCP_ENDPOINT_NOT_FOUND") {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds - prevent excessive refetching
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
  });
}

export function useMcpResources(projectId?: string, enabled = true) {
  return useQuery({
    queryKey: ["MCP", projectId, "resources"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      return fetchMcpResources(projectId);
    },
    enabled: enabled && !!projectId,
    retry: (failureCount, error) => {
      // Don't retry if MCP endpoint is not found (deployment issue)
      if (error?.message === "MCP_ENDPOINT_NOT_FOUND") {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds - prevent excessive refetching
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
  });
}

export function useMcpPrompts(projectId?: string, enabled = true) {
  return useQuery({
    queryKey: ["MCP", projectId, "prompts"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      return fetchMcpPrompts(projectId);
    },
    enabled: enabled && !!projectId,
    retry: (failureCount, error) => {
      // Don't retry if MCP endpoint is not found (deployment issue)
      if (error?.message === "MCP_ENDPOINT_NOT_FOUND") {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds - prevent excessive refetching
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
  });
}

export function useMcpToolsWithCapabilities(
  projectId?: string,
  enabled = true,
) {
  // Get initialization state directly from Zustand (no TanStack Query wrapper)
  const initialization = useMcpSessionStore((state) =>
    projectId ? state.getInitialization(projectId) : undefined,
  );
  const session = useMcpSessionStore((state) =>
    projectId ? state.getSession(projectId) : undefined,
  );

  const hasToolsCapability = hasCapability(initialization, "tools");
  const isSessionReady = session?.status === "connected";
  const hasInitialization = !!initialization;

  // Simpler enabling logic: enabled + projectId + (session ready OR has init data)
  const shouldTryFetch =
    enabled && !!projectId && (isSessionReady || hasInitialization);

  // Debug logging for query enabling
  if (process.env.NODE_ENV === "development" && projectId) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      console.log("MCP Tools Query Debug:", {
        projectId,
        enabled,
        sessionStatus: session?.status,
        hasInitialization,
        shouldTryFetch,
        hasCapability: hasToolsCapability,
      });
    }, [
      projectId,
      enabled,
      session?.status,
      hasInitialization,
      shouldTryFetch,
      hasToolsCapability,
    ]);
  }

  const toolsQuery = useQuery({
    queryKey: ["MCP", projectId, "tools"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      console.log("MCP: Fetching tools for project", projectId);
      return fetchMcpTools(projectId);
    },
    enabled: shouldTryFetch,
    retry: (failureCount, error) => {
      // Don't retry if it's a capability-related error
      if (error?.message?.includes("tools/list") && !hasToolsCapability) {
        return false;
      }
      // Don't retry if MCP endpoint is not found (deployment issue)
      if (error?.message === "MCP_ENDPOINT_NOT_FOUND") {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds - prevent excessive refetching
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
  });

  return {
    ...toolsQuery,
    initData: initialization,
    hasToolsCapability,
    isInitializing: !hasInitialization && session?.status !== "error",
    initError:
      session?.status === "error"
        ? new Error(session.error || "Session error")
        : undefined,
  };
}

export function useMcpResourcesWithCapabilities(
  projectId?: string,
  enabled = true,
) {
  // Get initialization state directly from Zustand (no TanStack Query wrapper)
  const initialization = useMcpSessionStore((state) =>
    projectId ? state.getInitialization(projectId) : undefined,
  );
  const session = useMcpSessionStore((state) =>
    projectId ? state.getSession(projectId) : undefined,
  );

  const hasResourcesCapability = hasCapability(initialization, "resources");
  const isSessionReady = session?.status === "connected";
  const hasInitialization = !!initialization;

  // Simpler enabling logic: enabled + projectId + (session ready OR has init data)
  const shouldTryFetch =
    enabled && !!projectId && (isSessionReady || hasInitialization);

  const resourcesQuery = useQuery({
    queryKey: ["MCP", projectId, "resources"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      return fetchMcpResources(projectId);
    },
    enabled: shouldTryFetch,
    retry: (failureCount, error) => {
      // Don't retry if it's a capability-related error
      if (
        error?.message?.includes("resources/list") &&
        !hasResourcesCapability
      ) {
        return false;
      }
      // Don't retry if MCP endpoint is not found (deployment issue)
      if (error?.message === "MCP_ENDPOINT_NOT_FOUND") {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds - prevent excessive refetching
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
  });

  return {
    ...resourcesQuery,
    initData: initialization,
    hasResourcesCapability,
    isInitializing: !hasInitialization && session?.status !== "error",
    initError:
      session?.status === "error"
        ? new Error(session.error || "Session error")
        : undefined,
  };
}

export function useMcpPromptsWithCapabilities(
  projectId?: string,
  enabled = true,
) {
  // Get initialization state directly from Zustand (no TanStack Query wrapper)
  const initialization = useMcpSessionStore((state) =>
    projectId ? state.getInitialization(projectId) : undefined,
  );
  const session = useMcpSessionStore((state) =>
    projectId ? state.getSession(projectId) : undefined,
  );

  const hasPromptsCapability = hasCapability(initialization, "prompts");
  const isSessionReady = session?.status === "connected";
  const hasInitialization = !!initialization;

  // Simpler enabling logic: enabled + projectId + (session ready OR has init data)
  const shouldTryFetch =
    enabled && !!projectId && (isSessionReady || hasInitialization);

  const promptsQuery = useQuery({
    queryKey: ["MCP", projectId, "prompts"],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      return fetchMcpPrompts(projectId);
    },
    enabled: shouldTryFetch,
    retry: (failureCount, error) => {
      // Don't retry if it's a capability-related error
      if (error?.message?.includes("prompts/list") && !hasPromptsCapability) {
        return false;
      }
      // Don't retry if MCP endpoint is not found (deployment issue)
      if (error?.message === "MCP_ENDPOINT_NOT_FOUND") {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30 * 1000, // 30 seconds - prevent excessive refetching
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
  });

  return {
    ...promptsQuery,
    initData: initialization,
    hasPromptsCapability,
    isInitializing: !hasInitialization && session?.status !== "error",
    initError:
      session?.status === "error"
        ? new Error(session.error || "Session error")
        : undefined,
  };
}

export function useMcpResourceContent(
  projectId?: string,
  resourceUri?: string,
  enabled = true,
) {
  // Get initialization state directly from Zustand
  const initialization = useMcpSessionStore((state) =>
    projectId ? state.getInitialization(projectId) : undefined,
  );
  const session = useMcpSessionStore((state) =>
    projectId ? state.getSession(projectId) : undefined,
  );

  const hasResourcesCapability = hasCapability(initialization, "resources");
  const isSessionReady = session?.status === "connected";
  const hasInitialization = !!initialization;

  return useQuery({
    queryKey: ["MCP", projectId, "resources", "read", resourceUri],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      if (!resourceUri) {
        throw new Error("Resource URI is required");
      }
      return fetchResourceContent(projectId, resourceUri);
    },
    enabled:
      enabled &&
      !!projectId &&
      !!resourceUri &&
      (isSessionReady || hasInitialization) &&
      hasResourcesCapability,
  });
}
